// Backend-defined close codes. Anything else is generic (network, proxy, etc).
const CLOSE_REASONS = {
  1000: 'Fermeture normale',
  1001: 'Le serveur a coupé la connexion',
  1006: 'Connexion interrompue (réseau ou serveur indisponible)',
  1008: 'Token invalide ou expiré',
  1011: 'Erreur interne du serveur',
  4001: 'Authentification refusée',
  4002: 'Quota mensuel dépassé',
  4003: 'Client introuvable',
}

function wsCloseMessage(closeEvent, hadError) {
  const code = closeEvent?.code
  const reason = closeEvent?.reason
  const label = CLOSE_REASONS[code] || (hadError ? 'Connexion refusée par le serveur' : 'Connexion fermée')
  const parts = [label]
  if (typeof code === 'number') parts.push(`code ${code}`)
  if (reason) parts.push(`« ${reason} »`)
  return parts.join(' · ')
}

/** WebSocket wrapper for /ws/clarity/session. JWT goes in ?token= per backend protocol. */
export class ClaritySocket {
  constructor(baseUrl, token) {
    this._url = `${baseUrl}?token=${encodeURIComponent(token)}`
    this._raw = null
    this._listeners = { started: [], delta: [], final: [], error: [], close: [] }
  }

  open() {
    return new Promise((resolve, reject) => {
      console.log('[ClaritySocket] opening', this._url)
      this._raw = new WebSocket(this._url)
      this._raw.binaryType = 'blob'
      let opened = false
      let errored = false
      this._raw.onopen = () => { opened = true; resolve() }
      this._raw.onerror = () => { errored = true }
      this._raw.onmessage = (e) => {
        if (typeof e.data !== 'string') return
        let m
        try { m = JSON.parse(e.data) } catch { return }
        const arr = this._listeners[m.type]
        if (arr) for (const fn of arr) fn(m)
      }
      this._raw.onclose = (e) => {
        // If we never reached open, surface the close as the open() rejection
        // so the caller gets a real Error with the protocol close code rather
        // than the raw `error` Event (which stringifies to "[object Event]").
        if (!opened) {
          const err = new Error(wsCloseMessage(e, errored))
          err.code = e?.code
          err.reason = e?.reason
          err.wasClean = e?.wasClean
          reject(err)
        }
        for (const fn of this._listeners.close) fn(e)
      }
    })
  }

  on(type, fn) { this._listeners[type]?.push(fn) }

  start({ kind, client_id = '', consent_method = 'verbal', pre_seed_facts = null }) {
    const msg = { type: 'start', kind, client_id, consent_method }
    if (pre_seed_facts) msg.pre_seed_facts = pre_seed_facts
    this._send(msg)
  }
  pause()  { this._send({ type: 'pause' }) }
  resume() { this._send({ type: 'resume' }) }
  stop()   { this._send({ type: 'stop' }) }

  // Ships a client-side breadcrumb to the server log. Lets us trace the audio
  // capture pipeline on devices where the browser console isn't reachable
  // (notably iOS Safari). Dropped silently if the socket isn't open.
  debug(msg) { this._send({ type: 'debug', msg: String(msg).slice(0, 500) }) }

  sendAudio(blob) {
    if (this._raw?.readyState === 1) this._raw.send(blob)
  }

  close() { this._raw?.close() }

  _send(obj) {
    if (this._raw?.readyState === 1) this._raw.send(JSON.stringify(obj))
  }
}
